import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { setMessage } from '../../../../app/reducers/message-slice';
import { useAppSelector } from '../../../../app/store/hooks';
import {
  AvatarPicker,
  type IAvatarValue,
} from '../../../../app/components/AvatarPicker';
import avatar1 from '../../../../assets/images/avatars/avatar-man.svg';
import avatar2 from '../../../../assets/images/avatars/avatar-woman.svg';
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
} from '../../infrastructure/api/user.api';
import './UserSettingsPage.scss';

type UserGender = 'man' | 'woman';

interface IForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  gender: UserGender;
}

const avatarList = [
  { id: 1, src: avatar1, alt: 'avatar-1' },
  { id: 2, src: avatar2, alt: 'avatar-2' },
];

const defaultAvatar = (): IAvatarValue => ({
  src: avatarList[0].src,
  type: 'avatar',
  confirmed: true,
});

export const UserSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { data: profile } = useGetProfileQuery(undefined, {
    skip: !accessToken,
  });
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();

  const [avatar, setAvatar] = useState<IAvatarValue>(defaultAvatar);
  const [initialAvatar, setInitialAvatar] = useState<IAvatarValue>(defaultAvatar());

  const validationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string()
          .min(2, t('form.required', { ns: 'validation' }))
          .required(t('form.required', { ns: 'validation' })),
        lastName: Yup.string()
          .min(2, t('form.required', { ns: 'validation' }))
          .required(t('form.required', { ns: 'validation' })),
        email: Yup.string()
          .email(t('form.invalid_email', { ns: 'validation' }))
          .required(t('form.required', { ns: 'validation' })),
        phone: Yup.string().required(t('form.required', { ns: 'validation' })),
        gender: Yup.string()
          .oneOf(['man', 'woman'])
          .required(t('form.required', { ns: 'validation' })),
      }),
    [t],
  );

  const form = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      gender: profile?.gender === 'woman' ? 'woman' : 'man',
    },
    validationSchema,
    onSubmit: (values) => {
      if (isSaving) return;
      if (avatar.type === 'photo' && !avatar.confirmed) return;

      updateProfile({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        avatar: avatar.src,
        gender: values.gender,
      })
        .unwrap()
        .then(() => {
          setInitialAvatar(avatar);
          form.resetForm({ values });
          dispatch(
            setMessage({
              value: t('settings.user-settings-updated'),
              timeout: 3000,
              type: 'success',
            }),
          );
        })
        .catch(() => {
          dispatch(
            setMessage({
              value: t('settings.user-settings-update-error'),
              timeout: 5000,
              type: 'error',
            }),
          );
        });
    },
  });

  const isAvatarChanged = avatar.src !== initialAvatar.src;
  const hasChanges = form.dirty || isAvatarChanged;
  const canSubmit =
    form.isValid &&
    hasChanges &&
    (avatar.type === 'avatar' || Boolean(avatar.confirmed));

  const initializeAvatar = useCallback(() => {
    const imageUrl = profile?.imageUrl;
    if (imageUrl) {
      const isPreset = avatarList.some((item) => item.src === imageUrl);
      const next: IAvatarValue = {
        src: imageUrl,
        type: isPreset ? 'avatar' : 'photo',
        confirmed: true,
      };
      setAvatar(next);
      setInitialAvatar(next);
      return;
    }
    const fallback = defaultAvatar();
    setAvatar(fallback);
    setInitialAvatar(fallback);
  }, [profile?.imageUrl]);

  useEffect(() => {
    initializeAvatar();
  }, [initializeAvatar]);

  return (
    <Box className="user-settings-page">
      <p className="description fw-bold">{t('settings.my-settings-description')}</p>
      <form className="user-settings-form" onSubmit={form.handleSubmit}>
        <Flex className="content-wrapper" direction="row" gap="40px">
          <Flex direction="column" className="form-container" flex="1">
            <FormControl isRequired className="form-group">
              <FormLabel htmlFor="firstName">{t('common.firstName')}</FormLabel>
              <Input
                name="firstName"
                className={
                  form.errors.firstName && form.touched.firstName ? 'is-invalid' : ''
                }
                value={form.values.firstName}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.touched.firstName && form.errors.firstName && (
                <div className="error-message">{form.errors.firstName}</div>
              )}
            </FormControl>

            <FormControl isRequired className="form-group">
              <FormLabel htmlFor="lastName">{t('common.lastName')}</FormLabel>
              <Input
                name="lastName"
                className={
                  form.errors.lastName && form.touched.lastName ? 'is-invalid' : ''
                }
                value={form.values.lastName}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.touched.lastName && form.errors.lastName && (
                <div className="error-message">{form.errors.lastName}</div>
              )}
            </FormControl>

            <FormControl isRequired className="form-group">
              <FormLabel htmlFor="email">{t('common.email')}</FormLabel>
              <Input
                name="email"
                type="email"
                className={form.errors.email && form.touched.email ? 'is-invalid' : ''}
                value={form.values.email}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.touched.email && form.errors.email && (
                <div className="error-message">{form.errors.email}</div>
              )}
            </FormControl>

            <FormControl isRequired className="form-group">
              <FormLabel htmlFor="phone">{t('common.phone')}</FormLabel>
              <Input
                name="phone"
                className={form.errors.phone && form.touched.phone ? 'is-invalid' : ''}
                value={form.values.phone}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
              />
              {form.touched.phone && form.errors.phone && (
                <div className="error-message">{form.errors.phone}</div>
              )}
            </FormControl>

            <FormControl isRequired className="form-group">
              <FormLabel>{t('common.gender')}</FormLabel>
              <RadioGroup
                name="gender"
                value={form.values.gender}
                onChange={(value) => form.setFieldValue('gender', value)}
              >
                <Stack direction="row" gap="16px">
                  <Radio value="man">{t('common.gender-man')}</Radio>
                  <Radio value="woman">{t('common.gender-woman')}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          </Flex>

          <AvatarPicker
            value={avatar}
            avatarList={avatarList}
            outputMode="base64"
            onChange={setAvatar}
            onRemove={() => setAvatar(defaultAvatar())}
          />
        </Flex>

        <div className="navigation-buttons">
          <Button
            type="submit"
            isLoading={isSaving}
            isDisabled={!canSubmit}
            className="save-button"
            colorScheme="blue"
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Box>
  );
};

export default UserSettingsPage;
